import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openAlertDetails } from "../../../utils/appNavigation";
import Swal from "sweetalert2";
import { appStatus } from "../../../store/modules/app_status/actions";
import occurrencesService from "../../../services/occurrencesService";
import { richTextToPlainText } from "../../../utils/richText";
import RichTextView from "../../RichTextEditor/RichTextView";
import {
  Wrapper, Table, TableScroll, Thead, Th, Td, Row, Badge, StatusBadge,
  EmptyState, FiltersWrapper, FilterGroup, FilterButton, ViewButton,
  ReadStatusButton, ReadStatusFilter, ActionButtons, FilterRow, FilterLabel,
  UnreadIndicator, Title, HeaderSection, DesktopOnlyTable, MobileCards,
  MobileCard, MobileCardTop, MobileCardTitle, MobileCardMeta, MobileCardActions,
  DescriptionCell,
} from "./styles";

const TYPE_LABELS = {
  alert_ticket: "Resposta de Alerta",
  alert_request: "Solicitação de Alerta",
  user_request: "Solicitação de Usuário",
};

const TYPE_BADGE_COLOR = {
  alert_ticket: "#2563eb",
  alert_request: "#d97706",
  user_request: "#7c3aed",
};

const DATE_FILTERS = { TODAY: "today", LAST_7_DAYS: "last7days", LAST_6_MONTHS: "last6months", ALL: "all" };
const DATE_FILTER_LABELS = {
  [DATE_FILTERS.TODAY]: "Hoje",
  [DATE_FILTERS.LAST_7_DAYS]: "Últimos 7 dias",
  [DATE_FILTERS.LAST_6_MONTHS]: "Últimos 6 meses",
  [DATE_FILTERS.ALL]: "Todos",
};

const READ_STATUS = { READ: "read", UNREAD: "unread", ALL: "all" };

const normalizeKey = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .trim()
    .toLowerCase();

// Mapa canônico por tipo + chave normalizada
const STATUS_MAP = {
  user_request: {
    pendente: { label: "Pendente", color: "#d97706" },
    analise: { label: "Em análise", color: "#2563eb" },
    aceito: { label: "Aceito", color: "#16a34a" },
    recusado: { label: "Recusado", color: "#dc2626" },
  },
  alert_request: {
    pendente: { label: "Pendente", color: "#d97706" },
    created: { label: "Alerta criado", color: "#16a34a" },
    rejected: { label: "Escaneamento rejeitado", color: "#dc2626" },
    // display vindo do backend
    "alerta criado": { label: "Alerta criado", color: "#16a34a" },
    "escaneamento rejeitado": { label: "Escaneamento rejeitado", color: "#dc2626" },
  },
  alert_ticket: {
    equipe_enviada: { label: "Equipe enviada", color: "#2563eb" },
    respondido_processo_sucessao: { label: "Respondido / Processo de Sucessão", color: "#16a34a" },
    encerrado: { label: "Encerrado", color: "#6b7280" },
    // displays que o backend pode mandar via get_action_display()
    "equipe enviada": { label: "Equipe enviada", color: "#2563eb" },
    "respondido / processo de sucessao": { label: "Respondido / Processo de Sucessão", color: "#16a34a" },
    "respondido / processo de sucessão": { label: "Respondido / Processo de Sucessão", color: "#16a34a" },
    "encerrado": { label: "Encerrado", color: "#6b7280" },
  },
};

const FALLBACK = { label: "—", color: "#6b7280" };

export const getStatusMeta = (type, status, resolution) => {
  const map = STATUS_MAP[type] || {};

  // alert_request: tenta pela resolution primeiro
  if (type === "alert_request" && resolution) {
    const r = map[normalizeKey(resolution)];
    if (r) return r;
  }

  // tenta pelo status bruto (funciona para "PENDENTE", "pendente", "Pendente", "created", etc.)
  const byStatus = map[normalizeKey(status)];
  if (byStatus) return byStatus;

  // fallback: mostra o texto original, se houver
  if (status) return { label: status, color: "#6b7280" };
  return FALLBACK;
};

export default function UnifiedOccurrencesTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.ALL);
  const [readFilter, setReadFilter] = useState(READ_STATUS.ALL);
  const [selectedType, setSelectedType] = useState(null);
  const [openingAlertId, setOpeningAlertId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadOccurrences = async () => {
    try {
      setLoading(true);
      const data = await occurrencesService.fetchOccurrences();
      setOccurrences(data);
      setError(null);
    } catch (err) {
      console.error("Erro ao buscar ocorrências:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOccurrences();
  }, []);

  const unifiedRows = useMemo(() => {
    return (occurrences || [])
      .filter((o) => o.data) // descarta entradas sem payload (segurança)
      .map((o) => ({
        id: o.id, // uuid do RequestSeen — usado pro toggle
        type: o.kind, // "user_request" | "alert_request" | "alert_ticket"
        title: o.data.title,
        description: o.data.description,
        status: o.data.status,
        resolution: o.data.resolution,
        date: o.data.date,
        seen: o.seen,
        originalData: o.data,
      }))
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
  }, [occurrences]);

  const filterByDate = (rows) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return rows.filter((row) => {
      if (!row.date) return dateFilter === DATE_FILTERS.ALL;
      const rowDate = new Date(row.date);
      const rowDateOnly = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());

      switch (dateFilter) {
        case DATE_FILTERS.TODAY:
          return rowDateOnly.getTime() === today.getTime();
        case DATE_FILTERS.LAST_7_DAYS: {
          const sevenDaysAgo = new Date(now);
          sevenDaysAgo.setDate(now.getDate() - 7);
          return rowDate >= sevenDaysAgo;
        }
        case DATE_FILTERS.LAST_6_MONTHS: {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(now.getMonth() - 6);
          return rowDate >= sixMonthsAgo;
        }
        default:
          return true;
      }
    });
  };

  const filterByType = (rows) => (!selectedType ? rows : rows.filter((r) => r.type === selectedType));

  const filterByReadStatus = (rows) => {
    if (readFilter === READ_STATUS.ALL) return rows;
    return rows.filter((row) => (readFilter === READ_STATUS.READ ? row.seen : !row.seen));
  };

  const filteredRows = useMemo(() => {
    let rows = unifiedRows;
    rows = filterByDate(rows);
    rows = filterByType(rows);
    rows = filterByReadStatus(rows);
    return rows;
  }, [unifiedRows, dateFilter, selectedType, readFilter]);

  const formatDate = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  const handleToggleRead = async (id) => {
    if (togglingId) return;
    try {
      setTogglingId(id);
      const updated = await occurrencesService.toggleOccurrenceRead(id);
      setOccurrences((prev) =>
        prev.map((o) => (o.id === id ? { ...o, ...updated } : o))
      );
    } catch (err) {
      console.error("Erro ao alternar leitura:", err);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível atualizar o status de leitura.",
        icon: "error",
      });
    } finally {
      setTogglingId(null);
    }
  };

  const renderStatus = (row) => {
    const { label, color } = getStatusMeta(row.type, row.status, row.resolution);
    return <StatusBadge $color={color}>{label}</StatusBadge>;
  };

  const handleViewTicket = (data) => {
    openAlertDetails(dispatch, navigate, { ticketId: data.ticket_id });
  };

  const handleViewAlertRequest = (data) => {
    dispatch(appStatus("Guardião"));
    navigate("/view_alert_request", { state: { alertRequest: data } });
  };

  const handleViewUserRequest = (data) => {
    navigate("/users", { state: { requestUser: data, activeTab: "requests" } });
  };

  const handleViewDetails = (row) => {
    switch (row.type) {
      case "alert_ticket":
        handleViewTicket(row.originalData);
        break;
      case "alert_request":
        handleViewAlertRequest(row.originalData);
        break;
      case "user_request":
        handleViewUserRequest(row.originalData);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <EmptyState>Carregando ocorrências...</EmptyState>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <EmptyState $error>Não foi possível carregar os dados. Tente novamente em instantes.</EmptyState>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeaderSection>
        <Title>Últimas ocorrências</Title>
      </HeaderSection>
      <FiltersWrapper>
        <FilterRow>
          <FilterLabel>Período:</FilterLabel>
          <FilterGroup>
            {Object.values(DATE_FILTERS).map((filter) => {
              if (filter === DATE_FILTERS.ALL) return null;
              return (
                <ReadStatusFilter key={filter} $active={dateFilter === filter} onClick={() => setDateFilter(filter)}>
                  {DATE_FILTER_LABELS[filter]}
                </ReadStatusFilter>
              );
            })}
            <FilterButton $active={dateFilter === DATE_FILTERS.ALL} onClick={() => setDateFilter(DATE_FILTERS.ALL)}>
              {DATE_FILTER_LABELS[DATE_FILTERS.ALL]}
            </FilterButton>
          </FilterGroup>
        </FilterRow>

        <FilterRow>
          <FilterLabel>Tipo:</FilterLabel>
          <FilterGroup>
            {Object.entries(TYPE_LABELS).map(([type, label]) => (
              <ReadStatusFilter key={type} $active={selectedType === type} onClick={() => setSelectedType(type)}>
                {label}
              </ReadStatusFilter>
            ))}
            <FilterButton $active={selectedType === null} onClick={() => setSelectedType(null)}>
              Todos
            </FilterButton>
          </FilterGroup>
        </FilterRow>

        <FilterRow>
          <FilterLabel>Status:</FilterLabel>
          <FilterGroup>
            <ReadStatusFilter $active={readFilter === READ_STATUS.UNREAD} onClick={() => setReadFilter(READ_STATUS.UNREAD)}>
              Não Lidos
            </ReadStatusFilter>
            <ReadStatusFilter $active={readFilter === READ_STATUS.READ} onClick={() => setReadFilter(READ_STATUS.READ)}>
              Lidos
            </ReadStatusFilter>
            <FilterButton $active={readFilter === READ_STATUS.ALL} onClick={() => setReadFilter(READ_STATUS.ALL)}>
              Todos
            </FilterButton>
          </FilterGroup>
        </FilterRow>
      </FiltersWrapper>

      {filteredRows.length === 0 ? (
        <TableScroll>
          <EmptyState>Não há ocorrências ou solicitações para exibir no momento.</EmptyState>
        </TableScroll>
      ) : (
        <TableScroll>
          <DesktopOnlyTable>
            <Table>
              <Thead>
                <tr>
                  <Th></Th>
                  <Th>Tipo</Th>
                  <Th>Título / Nome</Th>
                  <Th>Descrição</Th>
                  <Th>Status</Th>
                  <Th>Data</Th>
                  <Th>Ações</Th>
                </tr>
              </Thead>
              <tbody>
                {filteredRows.map((row) => (
                  <Row key={row.id} $isRead={row.seen}>
                    <Td>{!row.seen && <UnreadIndicator />}</Td>
                    <Td>
                      <Badge $color={TYPE_BADGE_COLOR[row.type]}>{TYPE_LABELS[row.type]}</Badge>
                    </Td>
                    <Td>{row.title}</Td>
                    <DescriptionCell title={richTextToPlainText(row.description)}>
                      <RichTextView value={row.description} fallback="—" />
                    </DescriptionCell>
                    <Td>
                      {renderStatus(row)}
                    </Td>
                    <Td>{formatDate(row.date)}</Td>
                    <Td>
                      <ActionButtons>
                        <ReadStatusButton
                          onClick={() => handleToggleRead(row.id)}
                          $isRead={row.seen}
                          disabled={togglingId === row.id}
                        >
                          {row.seen ? "Marcar como não lido" : "Marcar como lido"}
                        </ReadStatusButton>
                        <ViewButton onClick={() => handleViewDetails(row)}>Visualizar</ViewButton>
                      </ActionButtons>
                    </Td>
                  </Row>
                ))}
              </tbody>
            </Table>
          </DesktopOnlyTable>

          <MobileCards>
            {filteredRows.map((row) => (
              <MobileCard key={`m-${row.id}`}>
                <MobileCardTop>
                  <Badge $color={TYPE_BADGE_COLOR[row.type]}>{TYPE_LABELS[row.type]}</Badge>
                  {!row.seen && <UnreadIndicator />}
                </MobileCardTop>
                <MobileCardTitle>{row.title}</MobileCardTitle>
                <MobileCardMeta as="div">
                  <RichTextView value={row.description} fallback="—" />
                </MobileCardMeta>
                <MobileCardMeta>{formatDate(row.date)}</MobileCardMeta>

                {/* 👇 AQUI ESTÁ A CORREÇÃO: Substituímos o bloco antigo por isso */}
                <div>
                  {renderStatus(row)}
                </div>

                <MobileCardActions>
                  <ReadStatusButton onClick={() => handleToggleRead(row.id)} $isRead={row.seen} disabled={togglingId === row.id}>
                    {row.seen ? "Não lido" : "Lido"}
                  </ReadStatusButton>
                  <ViewButton onClick={() => handleViewDetails(row)}>Visualizar</ViewButton>
                </MobileCardActions>
              </MobileCard>
            ))}
          </MobileCards>
        </TableScroll>
      )}
    </Wrapper>
  );
}